import type { ShaderProgram, Material } from "../types/fluid-types.js"
import { compileShader, createProgram, getUniforms } from "./webgl-context.js"

/**
 * Creates a shader program wrapper with uniform management.
 */
export class Program implements ShaderProgram {
	public program: WebGLProgram
	public uniforms: Record<string, WebGLUniformLocation>
	private gl: WebGLRenderingContext | WebGL2RenderingContext

	constructor(
		gl: WebGLRenderingContext | WebGL2RenderingContext,
		vertexShader: WebGLShader,
		fragmentShader: WebGLShader
	) {
		this.gl = gl
		const program = createProgram(gl, vertexShader, fragmentShader)
		if (!program) {
			throw new Error("Failed to create shader program")
		}

		this.program = program
		this.uniforms = getUniforms(gl, program)
	}

	bind(): void {
		this.gl.useProgram(this.program)
	}
}

/**
 * Material class for managing shaders with keyword variants.
 */
export class MaterialImpl implements Material {
	public program: WebGLProgram
	public uniforms: Record<string, WebGLUniformLocation>

	private programs: Map<string, WebGLProgram> = new Map()
	private gl: WebGLRenderingContext | WebGL2RenderingContext
	private vertexShader: WebGLShader
	private fragmentShaderSource: string

	constructor(
		gl: WebGLRenderingContext | WebGL2RenderingContext,
		vertexShader: WebGLShader,
		fragmentShaderSource: string
	) {
		this.gl = gl
		this.vertexShader = vertexShader
		this.fragmentShaderSource = fragmentShaderSource

		// Create default program (no keywords)
		this.program = this.createProgramVariant([])
		this.uniforms = getUniforms(gl, this.program)
	}

	private createProgramVariant(keywords: string[]): WebGLProgram {
		const keywordString = keywords.join("_")

		if (this.programs.has(keywordString)) {
			return this.programs.get(keywordString)!
		}

		// Add #define statements for keywords
		let fragmentSource = this.fragmentShaderSource
		if (keywords.length > 0) {
			const defines = keywords.map((keyword) => `#define ${keyword}`).join("\n")
			fragmentSource = defines + "\n" + fragmentSource
		}

		const fragmentShader = compileShader(
			this.gl,
			this.gl.FRAGMENT_SHADER,
			fragmentSource
		)
		if (!fragmentShader) {
			throw new Error("Failed to compile fragment shader variant")
		}

		const program = createProgram(this.gl, this.vertexShader, fragmentShader)
		if (!program) {
			throw new Error("Failed to create program variant")
		}

		this.programs.set(keywordString, program)
		return program
	}

	setKeywords(keywords: string[]): void {
		this.program = this.createProgramVariant(keywords)
		this.uniforms = getUniforms(this.gl, this.program)
	}

	bind(): void {
		this.gl.useProgram(this.program)
	}
}

/**
 * Helper function to create a basic program.
 */
export function createBasicProgram(
	gl: WebGLRenderingContext | WebGL2RenderingContext,
	vertexShaderSource: string,
	fragmentShaderSource: string
): Program {
	const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
	const fragmentShader = compileShader(
		gl,
		gl.FRAGMENT_SHADER,
		fragmentShaderSource
	)

	if (!vertexShader || !fragmentShader) {
		throw new Error("Failed to compile shaders")
	}

	return new Program(gl, vertexShader, fragmentShader)
}

/**
 * Helper function to create a material.
 */
export function createMaterial(
	gl: WebGLRenderingContext | WebGL2RenderingContext,
	vertexShaderSource: string,
	fragmentShaderSource: string
): MaterialImpl {
	const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource)

	if (!vertexShader) {
		throw new Error("Failed to compile vertex shader")
	}

	return new MaterialImpl(gl, vertexShader, fragmentShaderSource)
}
